import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {icons} from "../../icons/Icons";
import {Rect} from "../../utils/Rect";
import * as i18n from "../../i18n/I18n";
import * as array from "../../utils/array";
import {ConfigService} from "../../services/ConfigService";
import {inject} from "../../services/di";
import {HistoryService} from "../../services/HistoryService";
import {EventService} from "../../services/EventService";

class CustomDOMException {
	static codeToName: { [code: number]: string } = {
		11: "InvalidStateError"
	};

	constructor(code: number, message: string) {
		Object.defineProperty(this, "code", {
			value: code,
			writable: false
		});

		Object.defineProperty(this, "message", {
			value: message,
			writable: false
		});

		Object.defineProperty(this, "name", {
			value: CustomDOMException.codeToName[code],
			writable: false
		});

		Object.defineProperty(this, "toString", {
			value: function() { return `${this.name}: ${this.message}.`; }
		});
	}
}

CustomDOMException.prototype = (function() {
	// Manipulate the DOM to throw a DOMException
	try { document.querySelectorAll("div:ex"); } catch (e) { return e; }
})();


const BASE_ZINDEX = 10;
const _stack: Dialog[] = [];

/**
 * Parse the given string and compute an anchor point position on the given
 * rectangle. Only percentage units and pixel units are supported for the
 * length values.
 *
 * @see http://www.w3.org/TR/html51/interactive-elements.html#anchor-points
 * @see http://www.w3.org/TR/2012/CR-css3-background-20120724/#the-background-position
 */
function parsePositionString(position: string, rect: ClientRect | Rect) {
	if (!position || position.length === 0)
		return null;

	// Parse a pair of position tokens to a positon on the range
	// [origin, origin + length].
	const parsePositionTokens = function(
			token1: string, token2: string, origin: number,
			length: number) {
		let positionDirection = 1;
		let positionOrigin: number;
		if (token1 === "left" || token1 === "top") {
			positionOrigin = origin;
		} else if (token1 === "right" || token1 === "bottom") {
			positionOrigin = origin + length;
			positionDirection = -1;
		} else {
			positionOrigin = origin + 0.5 * length;
		}

		const positionOffset = parseFloat(token2);
		if (isNaN(positionOffset))
			return positionOrigin;

		const percentage = token2[token2.length - 1] === "%";
		if (percentage)
			return positionOrigin + 0.01 * positionDirection *
					positionOffset * length;
		else
			return positionOrigin + positionDirection * positionOffset;
	};

	// Parse a single position token to a position on the range
	// [origin, origin + length].
	const parsePositionToken =
			function(token: string, origin: number, length: number) {
				if (token === "left" || token === "right" || token === "center" ||
						token === "top" || token === "bottom")
					return parsePositionTokens(token, "0", origin, length);
				else
					return parsePositionTokens("left", token, origin, length);
			};

	const tokens = position.split(/\s+/);
	if (tokens.length === 1) {
		return [parsePositionToken(tokens[0], rect.left, rect.width),
						rect.top + 0.5 * rect.height];
	} else if (tokens.length === 2) {
		return [parsePositionToken(tokens[0], rect.left, rect.width),
						parsePositionToken(tokens[1], rect.top, rect.height)];
	} else if (tokens.length === 3) {
		if (tokens[1] === "top" || tokens[1] === "bottom" ||
				tokens[1] === "center")
			return [parsePositionToken(tokens[0], rect.left, rect.width),
							parsePositionTokens(tokens[1], tokens[2], rect.top,
																	rect.height)];
		else
			return [parsePositionTokens(tokens[0], tokens[1], rect.left,
																	rect.width),
							parsePositionToken(tokens[2], rect.top, rect.height)];
	} else {
		return [parsePositionTokens(tokens[0], tokens[1],
																rect.left, rect.width),
						parsePositionTokens(tokens[2], tokens[3],
																rect.top, rect.height)];
	}
}

/**
 * Given a rectangle, an anchor point for that rectangle, and
 * a second rectangle, return an anchor point on the second
 * rectangle appropriate for the anchor point on the first.
 *
 * This finds which quadrant the anchor point lies in and
 * returns an anchor point to align the second rectangle with
 * that quadrant.
 *
 *           center bottom
 *              --------
 *              |\ N  /|
 *              | \  / |
 * right center |W \/ E| left center
 *              |  /\  |
 *              | /  \ |
 *              |/ S  \|
 *              --------
 *             center top
 *
 */
function findAnchor(anchorRect: ClientRect | Rect, anchorPoint: number[], otherRect: ClientRect | Rect) {
	const cx = anchorRect.left + 0.5 * anchorRect.width;
	const cy = anchorRect.top + 0.5 * anchorRect.height;
	if (anchorPoint[0] === cx && anchorPoint[1] === cy)
		return [otherRect.left + 0.5 * otherRect.width,
						otherRect.top + 0.5 * otherRect.height];

	const ne = [anchorRect.right, anchorRect.top];
	const nw = [anchorRect.left, anchorRect.top];

	const det1 = (((ne[0] - cx) * (anchorPoint[1] - cy)) -
								((ne[1] - cy) * (anchorPoint[0] - cx))) >= 0;
	const det2 = (((nw[0] - cx) * (anchorPoint[1] - cy)) -
								((nw[1] - cy) * (anchorPoint[0] - cx))) >= 0;

	if (det1) {
		if (det2) {
			return [otherRect.left,
							otherRect.top + 0.5 * otherRect.height];
		} else {
			return [otherRect.left + 0.5 * otherRect.width,
							otherRect.top];
		}
	} else {
		if (det2) {
			return [otherRect.left + 0.5 * otherRect.width,
							otherRect.bottom];
		} else {
			return [otherRect.right,
							otherRect.top + 0.5 * otherRect.height];
		}
	}
}


function focusFirstElement(el: HTMLElement) {
	if (document.documentElement.hasAttribute("touchscreen"))
		return;

	const autoFocusables =
			el.querySelectorAll("[autofocus]:not([disabled]):not([hidden])");
	for (let i = 0; i < autoFocusables.length; ++i) {
		const focusable = autoFocusables.item(i);
		if (focusable instanceof HTMLElement) {
			const styles = getComputedStyle(focusable);
			if (styles.display !== "none" &&
					styles.visibility !== "hidden" &&
					styles.opacity !== "0") {
				focusable.focus();
				return;
			}
		}
	}

	const orderedFocusables =
			el.querySelectorAll("[tabindex]:not([disabled]):not([hidden])," +
													"button:not([disabled]):not([hidden])," +
													"a:not([disabled]):not([hidden])," +
													"input:not([disabled]):not([hidden])," +
													"select:not([disabled]):not([hidden])," +
													"textarea:not([disabled]):not([hidden])");
	for (let i = 0; i < orderedFocusables.length; ++i) {
		const focusable = orderedFocusables.item(i);
		if (focusable instanceof HTMLElement) {
			const styles = getComputedStyle(focusable);
			if (styles.display !== "none" &&
					styles.visibility !== "hidden" &&
					styles.opacity !== "0") {
				focusable.focus();
				return;
			}
		}
	}
}

@element("flipp-dialog-placeholder")
class DialogPlaceHolder extends Component<{}, {}> {
	onDetach() {
		this.dispatchEvent(new Event("detach"));
	}
}

// Public Definitions -------------------------------------------------------->

@element("flipp-dialog-header")
export class DialogHeader extends Component<{}, {}> {
	@inject config: ConfigService;

	render() {
		return <template>
			<header flex-horizontal  style={{backgroundColor: this.config.getThemeColor()}}>
				<button class="back-button"
								title={i18n.i18n("close_dialog_button")}
								aria-label={i18n.i18n("close_dialog_button")}
								onClick={this._handleCloseButtonClick}>
					{icons.backArrow}
					{icons.flippLogo}
				</button>
				<div id="dialog-title" class="title">
					<content slot="title" />
				</div>
				<button class="close-button"
								title={i18n.i18n("close_dialog_button")}
								aria-label={i18n.i18n("close_dialog_button")}
								onClick={this._handleCloseButtonClick}>
						{icons.searchClose}
				</button>
			</header>
		</template>;
	}

	private _handleCloseButtonClick = (e: Event) => {
		this.dispatchEvent(new Event("flipp-dialog-close", {
			bubbles: true,
			cancelable: false
		}));
	}
}

interface DialogProperties extends JSX.HTMLProps {
	popover?: boolean;
	transient?: boolean;
	onClose?: JSX.GenericEventHandler;
}

@element("flipp-dialog")
export class Dialog extends Component<DialogProperties, {}> {
	@inject config: ConfigService;
	@inject eventService: EventService;
	@inject historyService: HistoryService;
	@attr transient: boolean;
	refs: {
		content: HTMLElement
	};

	protected _zIndex = BASE_ZINDEX;
	private _placeholder: HTMLElement;
	private _lastActive: HTMLElement = null;
	private _inDocument = false;
	private _anchor: MouseEvent|Element = null;

	get open() {
		return this.hasAttribute("open");
	}

	get modal() {
		return this.hasAttribute("modal");
	}

	set onclose(handler: JSX.GenericEventHandler) {
		this.addEventListener("close", handler);
	}

	onCreate() {
		/*
			Disable virtual dom rendering to create the placeholder. This is so the local _placeholder
			is an Element which can be manually swapped into the DOM
		*/
		flipp.setVirtualDOM(false);
		this._placeholder = <DialogPlaceHolder />;
		flipp.setVirtualDOM(true);
	}

	onAttach() {
		this._inDocument = true;

		window.addEventListener("resize", this._handleResize);
		this.addEventListener("click", this._handleBackgroundClick);
		window.addEventListener("keyup", this._handleKeyUp);
		this._placeholder.addEventListener("detach",
				this._handlePlaceholderDetach);
		this.addEventListener("flipp-dialog-close",
				this._handleDialogCloseEvent);
	}

	onDetach() {
		this._inDocument = false;

		window.removeEventListener("resize", this._handleResize);
		this.removeEventListener("click", this._handleBackgroundClick);
		window.removeEventListener("keyup", this._handleKeyUp);
		this._placeholder.removeEventListener("detach",
				this._handlePlaceholderDetach);
		this.removeEventListener("flipp-dialog-close",
				this._handleDialogCloseEvent);
	}

	render() {
		return <template>
			<div class="content"
					ref="content"
					role="dialog"
					aria-labelledby="dialog-title"
					flex-vertical>
				<content />
			</div>
		</template>;
	}

	onRender() {
		if (this.open) {
			this.style.zIndex = this._zIndex.toString();
			this._positionDialogElement();
		}
	}

	onAttributeChange(name: string, oldVal: string, newVal: string) {
		if (name === "data-flipp-inert" || name === "aria-hidden") return;
		super.onAttributeChange(name, oldVal, newVal);
	}

	show(anchor: MouseEvent|Element) {
		throw new Error("Not implemented");
	}

	/**
	 * https://www.w3.org/TR/html51/interactive-elements.html#dom-htmldialogelement-showmodal
	 * @param  {[type]} anchor: MouseEvent|Element Optional anchor element/event.
	 */
	showModal(anchor?: MouseEvent|Element) {
		if (this.open)
			throw new CustomDOMException(DOMException.INVALID_STATE_ERR, "Dialog already open");

		this._ensureAttached();
		if (!this._inDocument && !this.transient)
			throw new CustomDOMException(DOMException.INVALID_STATE_ERR, "Dialog not in document");

		if (this.ownerDocument.activeElement instanceof HTMLElement) {
			this._lastActive = this.ownerDocument.activeElement as HTMLElement;
		}

		this.setAttribute("open", "");
		this.setAttribute("modal", "");

		this._anchor = anchor || null;

		// block
		this._zIndex = _stack[0] ?
				_stack[0]._zIndex + 1 : BASE_ZINDEX;
		_stack.unshift(this);

		const doc = this.ownerDocument;
		// doc.body.setAttribute('aria-hidden', 'true');

		// insert into 'pending stack'
		if (!this.transient) {
			this.parentNode.replaceChild(this._placeholder, this as Node);
		}

		doc.body.insertBefore(this as Node, doc.body.firstChild);

		// disable tab index
		this._setDocumentInert(true);
		focusFirstElement(this as any);

		// Ensure children are rendered, then position
		this.invalidate().then(() => {
			// position & focus
			this._positionDialogElement();
			focusFirstElement(this as any);
		});
	}

	close(returnValue?: string) {
		if (!this.open)
			throw new CustomDOMException(DOMException.INVALID_STATE_ERR, "Dialog not open");

		// put the element back
		this.removeAttribute("open");
		this.removeAttribute("modal");

		array.remove(_stack, this);
		this._zIndex = BASE_ZINDEX;

		this._anchor = null;
		if (this.transient) {
			this.parentNode.removeChild(this as Node);
		} else {
			this._placeholder.parentNode.replaceChild(this as Node, this._placeholder);
		}

		this._setDocumentInert(false);
		if (_stack.length > 0) {
			_stack[0]._setDocumentInert(true);
		}

		// restore the last active
		if (this._lastActive !== null) {
			this._lastActive.focus();
			this._lastActive = null;
		}

		// only fire if transient or closed but attached to DOM
		if (this.transient ||
				!(this.compareDocumentPosition(document.documentElement) &
							document.DOCUMENT_POSITION_DISCONNECTED)) {
			this.dispatchEvent(new Event("close", {
				bubbles: true,
				cancelable: false
			}));
		}
	}

	private _handleBackgroundClick = (e: Event) => {
		if (this.modal && e.target === this) {
			this.close();
			this.eventService.changeUserLocationForm();
			this.eventService.unsubscribeFromRedirect();
		}
	}

	private _handleKeyUp = (e: KeyboardEvent) => {
		if (this.open && (e.key === "Escape" || e.keyCode === 27) &&
				_stack[0] === this) {
			this.close();
			this.eventService.changeUserLocationForm();
		}
	}

	private _handlePlaceholderDetach = (e: Event) => {
		// When modal, we are not in our normal location so we use the placeholder
		// to know when the DOM is manipulated at that position.
		if (this.open) this.close();
	}

	private _handleResize = (e: Event) => {
		if (this.open) this._positionDialogElement();
	}

	/**
	 * We can't rely on attachedCallback being called before since the polyfill
	 * using MutationObservers is async. i.e.
	 * ```
	 * node.appendChild(dialog);
	 * dialog.open(); // would throw because onAttach won't be called until
	 *                   next tick.
	 * ```
	 *
	 * But since `show` and `showModal` require synchronous attachedCallback we
	 * force it.
	 */
	private _ensureAttached() {
		if (!this._inDocument &&
				!(this.compareDocumentPosition(document.documentElement) &
						document.DOCUMENT_POSITION_DISCONNECTED)) {
			this.attachedCallback();
		}
	}

	/**
	 * Position dialog according to the specification rules.
	 */
	private _positionDialogElement = () => {
		let magicAlignment = false;
		let anchorPositionB: number[] = null;
		let anchorBoundingBoxB: Rect | ClientRect;
		if (this._anchor === undefined || this._anchor === null) {
			// // Normal alignment mode.
			// if (!this._inPendingDialogStack)
			//   return;

			anchorPositionB = [0.5 * window.innerWidth, 0.5 * window.innerHeight];
		} else if (this._anchor instanceof MouseEvent) {
			const mouseEvent = this._anchor as MouseEvent;
			const mouseEventTarget = mouseEvent.target as HTMLElement;
			if (!mouseEventTarget ||
					mouseEventTarget.style.display === "none" ||
					mouseEventTarget.style.visibility === "hidden" ||
					mouseEventTarget.ownerDocument !== document) {

				// Center alignment mode.
				anchorPositionB = [0.5 * window.innerWidth, 0.5 * window.innerHeight];
			} else {
				// Magic alignment from mouse position.
				anchorPositionB = [mouseEvent.clientX, mouseEvent.clientY];
				magicAlignment = true;
			}
			anchorBoundingBoxB =
					new Rect(anchorPositionB[0], anchorPositionB[1], 1, 1);
		} else {
			// Magic alignment anchoring to another element.
			const anchorElement = this._anchor as HTMLElement;
			anchorBoundingBoxB = anchorElement.getBoundingClientRect();
			const anchorPointPositionB =
					anchorElement.getAttribute("anchor-point");
			anchorPositionB = parsePositionString(
					anchorPointPositionB, anchorBoundingBoxB);
			magicAlignment = true;
		}

		// Get the dialog element's margin box, as described in the spec.
		const dialogBoundingBox = this.refs.content.getBoundingClientRect();
		const anchorBoundingBoxA =
				new Rect(
						dialogBoundingBox.left, dialogBoundingBox.top,
						dialogBoundingBox.width, dialogBoundingBox.height);

		// Default anchor point for centering mode.
		let anchorPositionA =
				[anchorBoundingBoxA.left + 0.5 * anchorBoundingBoxA.width,
				anchorBoundingBoxA.top + 0.5 * anchorBoundingBoxA.height];

		if (magicAlignment) {
			// In magic alignment mode, compute anchor points on the anchor
			// rectangle and dialog rectangle.
			anchorPositionA = parsePositionString(
					this.getAttribute("anchor-point"), anchorBoundingBoxA);

			if (!anchorPositionA && !anchorPositionB) {
				anchorPositionA =
						[anchorBoundingBoxA.left + 0.5 * anchorBoundingBoxA.width,
						anchorBoundingBoxA.top + 0.5 * anchorBoundingBoxA.height];
				anchorPositionB =
						[anchorBoundingBoxB.left + 0.5 * anchorBoundingBoxB.width,
						anchorBoundingBoxB.top + 0.5 * anchorBoundingBoxB.height];
			} else if (!anchorPositionA) {
				anchorPositionA = findAnchor(
						anchorBoundingBoxB, anchorPositionB,
						anchorBoundingBoxA);
			} else if (!anchorPositionB) {
				anchorPositionB = findAnchor(
						anchorBoundingBoxA, anchorPositionA,
						anchorBoundingBoxB);
			}
		}

		// The candidate rectangle for the dialog placement.
		const proposedRect =
				new Rect(
						anchorPositionB[0] - anchorPositionA[0] + anchorBoundingBoxA.left,
						anchorPositionB[1] - anchorPositionA[1] + anchorBoundingBoxA.top,
						dialogBoundingBox.width, dialogBoundingBox.height);

		if (proposedRect.right > this.offsetWidth) {
			proposedRect.left -= (proposedRect.right - this.offsetWidth);
		}

		// Apply the styling for the final placement.
		this.refs.content.style.left = proposedRect.left + "px";
		this.refs.content.style.top = proposedRect.top + "px";
	}

	private _setDocumentInert(inert: boolean) {
		let child = this.ownerDocument.body.firstElementChild;
		while (child) {
			if (child !== ((this as any) as Element)) {
				setInert(child, inert);
			}

			child = child.nextElementSibling;
		}
	}

	private _handleDialogCloseEvent = (e: Event) => {
		this.close();
	}
}


function setInert(element: Element, on: Boolean): void {
	const elements = element.getElementsByTagName("*");
	for (let i = 0, n = elements.length; i <= n; ++i) {
		const e: Element = i === n ? element : elements[i];

		if (on) {
			if (e.hasAttribute("data-flipp-tabindex")) continue;

			if (e.hasAttribute("tabindex")) {
				e.setAttribute("data-flipp-tabindex",
											e.getAttribute("tabindex").toString());

				switch (e.tagName) {
				case "BUTTON":
				case "A":
				case "INPUT":
				case "TEXTAREA":
				case "SELECT":
				case "IFRAME":
					e.setAttribute("tabindex", "-1");
					break;
				default:
					e.removeAttribute("tabindex");
					break;
				}
			} else {
				switch (e.tagName) {
				case "BUTTON":
				case "A":
				case "INPUT":
				case "TEXTAREA":
				case "SELECT":
				case "IFRAME":
					e.setAttribute("data-flipp-tabindex", "0");
					e.setAttribute("tabindex", "-1");
					break;
				default:
					break;
				}
			}
		} else if (e.hasAttribute("data-flipp-tabindex")) {
			const tabindex = e.getAttribute("data-flipp-tabindex");
			switch (e.tagName) {
			case "BUTTON":
			case "A":
			case "INPUT":
			case "TEXTAREA":
			case "SELECT":
			case "IFRAME":
				if (tabindex === "0")
					e.removeAttribute("tabindex");
				else
					e.setAttribute("tabindex", tabindex);
				break;
			default:
				e.setAttribute("tabindex", tabindex);
				break;
			}
			e.removeAttribute("data-flipp-tabindex");
		}
	}

	if (on) {
		element.setAttribute("data-flipp-inert", "");
		element.setAttribute("aria-hidden", "true");
	} else {
		element.removeAttribute("data-flipp-inert");
		element.removeAttribute("aria-hidden");
	}
}
